import { Autocomplete, TextField } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { ContextChooser } from "../../reusableComponents/contextSwitcher/contextSwitcher";
import {
  contextIdOf,
  toRecentContext,
} from "../../reusableComponents/contextSwitcher/contextSwitcherModel";
import type { ViewAsOption } from "../../reusableComponents/contextSwitcher/contextSwitcherModel";

import { RoleIndex } from "../../../models/role";
import { UserType } from "../../../models/userType";
import {
  Program,
  userTypeNeedsSalesRep,
  userTypeNeedsSchoolDealer,
} from "../../../models/program";

import {
  getClientPrograms,
  getClientSalesRep,
  getTrainingUsers,
  getUserClients,
} from "../../../store/thunks/portalManagementThunk";
import { logoutUser } from "../../../store/thunks/authenticationThunk";
import { logout } from "../../../store/features/authentication/authenticationSlice";
import { systemManagementReset } from "../../../store/features/systemManagement/systemManagementSlice";
import {
  portalManagementReset,
  portalManagementSearchReset,
  recordRecentContext,
  setContextCommitted,
  setDefaultContext,
  setFilteredUserTypes,
  setSelectedClient,
  setSelectedProgram,
  setSelectedSalesRep,
  setSelectedSchoolDealer,
  setSelectedUserType,
} from "../../../store/features/portalManagement/portalManagementSlice";

// Which extra scope a user type needs before the context is complete — the
// rule lives in models/program.ts, shared with the shell's recent restore.
const needsSalesRep = (t: any) => userTypeNeedsSalesRep(t?.client_user_type_index);
const needsSchoolDealer = (t: any) => userTypeNeedsSchoolDealer(t?.client_user_type_index);

const programLabelOf = (program: any) =>
  Program.DisplayName[program?.program_index as keyof typeof Program.DisplayName] ??
  program?.program_name ??
  "";

interface DashboardChooserProps {
  open: boolean;
  /** Omitted while no context exists — there is nothing to go back to. */
  onClose?: () => void;
  /** After a confirm — not cancel. Shell resets the rail to Dashboard. */
  onCommitted?: () => void;
}

/**
 * The dashboard chooser, replacing accountSelectionView.
 *
 * Owns the load sequence the old screen did — clients on open, programs per
 * client, user types filtered per program, sales reps on demand — but drives
 * the presentational <ContextChooser>, which shows one step at a time. The old
 * screen let you submit anything and then reported up to five errors at once;
 * here an unanswered step is simply the step you are on.
 */
const DashboardChooser = ({ open, onClose, onCommitted }: DashboardChooserProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { user } = useAppSelector((s: any) => s.authentication);
  const {
    clients,
    programs,
    userTypes,
    salesReps,
    trainingUsers,
    selectedClient,
    selectedProgram,
    selectedUserType,
    selectedSalesRep,
    selectedSchoolDealer,
    clientsLoading,
    programsLoading,
    salesRepsLoading,
    trainingUsersLoading,
  } = useAppSelector((s: any) => s.portalManagement);

  const isClientUser = user?.role_index === RoleIndex.ClientUser;

  // Draft, not the live context. The dashboard behind this dialog renders from
  // the committed selection, so editing a step here must not clear it — that
  // would tear down the screen the user is still looking at and strand Cancel.
  const [draftClient, setDraftClient] = useState<any>(null);
  const [draftProgram, setDraftProgram] = useState<any>(null);
  const [draftUserType, setDraftUserType] = useState<any>(null);
  const [draftScope, setDraftScope] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  // Reopening shows the context you are in, ready to be changed.
  useEffect(() => {
    if (!open) return;
    setDraftClient(selectedClient ?? null);
    setDraftProgram(selectedProgram ?? null);
    setDraftUserType(selectedUserType ?? null);
    setDraftScope(selectedSalesRep ?? selectedSchoolDealer ?? null);
    // Seeded once per opening; later edits are the user's, not the store's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isTraining = draftProgram?.program_index === Program.Index.Training;

  // Only LWS_ADMIN picks a user type in Training; the others have exactly one.
  const isLWSAdmin = useMemo(
    () =>
      (trainingUsers as any[] | undefined)?.find(
        (u: any) => u.clients_user_id === user?.client_user_index
      )?.role_type === UserType.Name.LWS_ADMIN,
    [trainingUsers, user]
  );
  const showViewAs = !isTraining || isLWSAdmin;


  // Latest-ref: the effect's guard reads current userTypes without re-firing
  // when getUserClients fills them.
  const userTypesRef = useRef(userTypes);
  userTypesRef.current = userTypes;

  useEffect(() => {
    // Skipping needs both of its assumptions: getUserClients is the only
    // writer of userTypes, so a snapshot with a seeded client but no types
    // (captured before types persisted) must still fetch, or the View-as
    // list stays empty and the chooser can never be completed.
    if (!open || (draftClient && userTypesRef.current?.length)) return;
    dispatch(getUserClients({}))
      .unwrap()
      .then((payload: any) => {
        if (draftClient?.client_index) {
          // Client already seeded; this run was for the user types.
          dispatch(getClientPrograms({ client_index: draftClient.client_index }));
          return;
        }
        // A client user has exactly one client; choosing it for them turns a
        // three-step flow into a two-step one.
        const only = payload?.user_clients?.[0];
        if (isClientUser && only) {
          setDraftClient(only);
          dispatch(getClientPrograms({ client_index: only.client_index }));
        }
      })
      .catch((err: any) => {
        if (err?.message !== "Rejected") return;
        dispatch(logoutUser({ user_index: user?.client_user_index }));
        dispatch(portalManagementReset());
        dispatch(systemManagementReset());
        dispatch(logout());
        navigate("/login");
      });
  }, [open, draftClient, dispatch, navigate, isClientUser, user]);

  useEffect(() => {
    if (open && isTraining && !trainingUsers?.length && !trainingUsersLoading) {
      dispatch(getTrainingUsers({}));
    }
  }, [open, isTraining, trainingUsers, trainingUsersLoading, dispatch]);

  const pickClient = (client: any) => {
    setDraftClient(client);
    if (!client?.client_index) return;
    dispatch(getClientPrograms({ client_index: client.client_index }));
  };

  const pickProgram = (program: any) => {
    setDraftProgram(program);
    setDraftUserType(null);
    setDraftScope(null);
  };

  // Derived, not dispatched: the store's filteredUserTypes belongs to the
  // committed context and must not follow a draft.
  const availableUserTypes = useMemo(
    () =>
      draftProgram?.program_index
        ? (userTypes ?? []).filter((t: any) => t.program_index === draftProgram.program_index)
        : [],
    [userTypes, draftProgram]
  );

  const viewAsOptions: ViewAsOption[] = useMemo(
    () =>
      showViewAs
        ? availableUserTypes.map((t: any) => ({
            id: String(t.client_user_type_index),
            title: t.label ?? t.name,
            body: "",
            code: t.name ?? "",
            userTypeIndex: t.client_user_type_index,
          }))
        : [],
    [showViewAs, availableUserTypes]
  );

  const pickViewAs = (id: string) => {
    const type = availableUserTypes.find(
      (t: any) => String(t.client_user_type_index) === id
    );
    setDraftUserType(type);
    setDraftScope(null);
    if (needsSalesRep(type)) {
      dispatch(getClientSalesRep({ customer_id: draftClient?.customer_id }));
    }
  };

  // Going back clears everything downstream too: a program belongs to a client
  // and a user type to a program, so keeping them would leave a context that
  // never existed.
  const clearFromProgram = () => {
    setDraftProgram(null);
    setDraftUserType(null);
    setDraftScope(null);
  };

  const editClient = () => {
    clearFromProgram();
    setDraftClient(null);
  };

  // The extra scope some user types need, rendered under the view-as step.
  const subScope = needsSalesRep(draftUserType) ? (
    <Autocomplete
      value={draftScope}
      onChange={(_, value) => setDraftScope(value)}
      options={salesReps ?? []}
      getOptionLabel={(o: any) => o?.SALESMAN ?? o?.name ?? ""}
      renderInput={(p) => <TextField {...p} label="Sales rep" />}
    />
  ) : needsSchoolDealer(draftUserType) ? (
    <Autocomplete
      value={draftScope}
      onChange={(_, value) => setDraftScope(value)}
      options={(trainingUsers ?? []).filter((u: any) => u.role_type === draftUserType?.name)}
      getOptionLabel={(o: any) => `${o.r_operand} (${o.first_name} ${o.last_name})`}
      renderInput={(p) => <TextField {...p} label="School or dealer" />}
    />
  ) : undefined;

  const scopeSatisfied =
    (!needsSalesRep(draftUserType) && !needsSchoolDealer(draftUserType)) || Boolean(draftScope);

  // The one place the draft becomes the live context.
  const confirm = (makeDefault: boolean) => {
    if (!scopeSatisfied) return;
    setBusy(true);
    const nextId = contextIdOf(draftClient, draftProgram, draftUserType);
    const currentId = contextIdOf(selectedClient, selectedProgram, selectedUserType);
    const sameScope =
      (!needsSalesRep(draftUserType) && !needsSchoolDealer(draftUserType)) ||
      (needsSalesRep(draftUserType) &&
        (draftScope?.SALESMAN ?? draftScope?.salesman) ===
          (selectedSalesRep?.SALESMAN ?? selectedSalesRep?.salesman)) ||
      (needsSchoolDealer(draftUserType) &&
        draftScope?.r_operand === selectedSchoolDealer?.r_operand);
    const sameContext = Boolean(nextId) && nextId === currentId && sameScope;

    // Re-opening the same context must not searchReset: that wipes tiles while
    // the aggregate session still thinks this customer is warm.
    if (!sameContext) {
      dispatch(portalManagementSearchReset());
      dispatch(setSelectedClient(draftClient));
      dispatch(setSelectedProgram(draftProgram));
      dispatch(setSelectedUserType(draftUserType));
      dispatch(setFilteredUserTypes(availableUserTypes));
      dispatch(needsSalesRep(draftUserType) ? setSelectedSalesRep(draftScope) : setSelectedSalesRep(null));
      dispatch(
        needsSchoolDealer(draftUserType)
          ? setSelectedSchoolDealer(draftScope)
          : setSelectedSchoolDealer(null)
      );
    }

    const context = toRecentContext({
      client: draftClient,
      program: draftProgram,
      userType: draftUserType,
      subScope: draftScope?.SALESMAN ?? draftScope?.r_operand,
      programLabel: programLabelOf(draftProgram),
    });
    dispatch(recordRecentContext(context));
    dispatch(setContextCommitted(true));
    if (makeDefault) dispatch(setDefaultContext(context));
    setBusy(false);
    onCommitted?.();
    onClose?.();
  };

  return (
    <ContextChooser
      open={open}
      onClose={onClose ?? (() => {})}
      onConfirm={confirm}
      showClientStep={!isClientUser}
      showMakeDefault={isClientUser}
      clientLabel={draftClient?.customer_name}
      programLabel={draftProgram ? programLabelOf(draftProgram) : undefined}
      viewAsOptions={viewAsOptions}
      viewAs={draftUserType ? String(draftUserType.client_user_type_index) : undefined}
      onViewAsChange={pickViewAs}
      onEditClient={editClient}
      onEditProgram={clearFromProgram}
      subScope={subScope}
      busy={busy || clientsLoading || programsLoading || salesRepsLoading || trainingUsersLoading}
      clientPicker={
        <Autocomplete
          value={draftClient}
          onChange={(_, value) => pickClient(value)}
          options={clients ?? []}
          getOptionLabel={(o: any) => o?.customer_name ?? ""}
          renderInput={(p) => <TextField {...p} label="Client" autoFocus />}
        />
      }
      programPicker={
        <Autocomplete
          value={draftProgram}
          onChange={(_, value) => pickProgram(value)}
          options={programs ?? []}
          getOptionLabel={programLabelOf}
          renderInput={(p) => <TextField {...p} label="Program" autoFocus />}
        />
      }
    />
  );
};

export default DashboardChooser;
